import { BookOpen, Mail, Phone, MapPin, Twitter, Linkedin, Youtube, Github } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-r from-green-900/10 to-transparent"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-600/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-teal-600/5 rounded-full blur-3xl"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Brand & Description */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center space-x-3 group">
                <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg group-hover:shadow-green-500/25 transition-shadow">
                  <BookOpen className="h-8 w-8 text-white" aria-label="BookOpen icon" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  ExecuteAutomation
                </span>
              </Link>
              <p className="mt-6 text-gray-300 max-w-md leading-relaxed">
                Transform your career with expert-led courses designed for real-world success. 
                Join thousands of professionals who've accelerated their growth with us.
              </p>
              
              {/* Social Links */}
              <div className="mt-8">
                <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">
                  Follow Us
                </h4>
                <div className="flex space-x-4">
                  {[
                    { icon: Twitter, href: 'http://x.com/executeauto', color: 'hover:text-blue-400' },
                    { icon: Linkedin, href: 'https://www.linkedin.com/in/karthikkk/', color: 'hover:text-blue-600' },
                    { icon: Youtube, href: 'http://youtube.com/executeautomation', color: 'hover:text-red-500' },
                    { icon: Github, href: '#', color: 'hover:text-gray-400' }
                  ].map(({ icon: Icon, href, color }, index) => (
                    <a
                      key={index}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-2 bg-green-800/50 rounded-lg text-gray-400 ${color} transition-all duration-300 hover:bg-green-700/50 hover:transform hover:scale-110`}
                    >
                      <Icon className="h-5 w-5" aria-label="Icon icon" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6 text-white">Learn</h3>
              <ul className="space-y-3">
                {[
                  { label: 'All Courses', href: '/courses' },
                  { label: 'AI Testing', href: '/courses?category=ai-testing' },
                  { label: 'Generative AI', href: '/courses?category=genai' },
                  { label: 'AI Development', href: '/courses?category=ai-development' },
                  { label: 'Free Resources', href: '/resources' },
                  { label: 'Learning Paths', href: '/paths' }
                ].map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center group"
                    >
                      <span className="w-0 group-hover:w-2 h-px bg-emerald-400 transition-all duration-200 mr-0 group-hover:mr-3"></span>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support & Legal */}
            <div>
              <h3 className="text-lg font-semibold mb-6 text-white">Support</h3>
              <ul className="space-y-3">
                {[
                  { label: 'About Us', href: '/about' },
                  { label: 'Contact Support', href: '/contact' },
                  { label: 'Help Center', href: '/help' },
                  { label: 'Privacy Policy', href: '/privacy' },
                  { label: 'Terms of Service', href: '/terms' },
                  { label: 'Refund Policy', href: '/refund' }
                ].map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center group"
                    >
                      <span className="w-0 group-hover:w-2 h-px bg-emerald-400 transition-all duration-200 mr-0 group-hover:mr-3"></span>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="border-t border-green-700/30 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Mail,
                title: 'Email Us',
                content: 'karthik@techgeek.co.in',
                subtext: 'We respond within 24 hours'
              },
              {
                icon: Phone,
                title: 'Call Us',
                content: '+1 (555) 123-4567',
                subtext: 'Mon-Fri, 9AM-6PM EST'
              },
              {
                icon: MapPin,
                title: 'Visit Us',
                content: 'Auckland, NZ',
                subtext: 'Remote-first company'
              }
            ].map((contact, index) => (
              <div key={index} className="flex items-start space-x-4 group">
                <div className="p-3 bg-green-800/50 rounded-xl text-emerald-400 group-hover:bg-emerald-600 group-hover:text-white transition-all duration-300">
                  <contact.icon className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">{contact.title}</h4>
                  <p className="text-gray-300 font-medium">{contact.content}</p>
                  <p className="text-sm text-gray-400">{contact.subtext}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-green-700/30 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <p className="text-gray-400">
                &copy; 2024 ExecuteAutomation. All rights reserved.
              </p>
              <div className="flex items-center space-x-4 text-sm">
                <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                  Privacy
                </Link>
                <span className="text-gray-600">•</span>
                <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                  Terms
                </Link>
                <span className="text-gray-600">•</span>
                <Link to="/cookies" className="text-gray-400 hover:text-white transition-colors">
                  Cookies
                </Link>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>All systems operational</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;